import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import type { LacUpdateUserInput, LacUser } from '../../types/lac';
import type { I18nContextValue } from '../../types/i18nContext';
import { pickUserOptimisticLockFields } from '../../utils/userUpdateUtils';
import type { LacClientRole } from './lacRoles';

type Translate = I18nContextValue['translate'];

type MutationLike<TVariables> = {
  mutate: (variables: TVariables, options?: object) => void;
};

type LacSetRolesVariables = {
  tenantName: string;
  userRef: string;
  roleNames: string[];
};

type LacUploadPhotoVariables = {
  tenantName: string;
  userRef: string;
  file: File;
};

type LacDeletePhotoVariables = {
  tenantName: string;
  userRef: string;
};

export function submitLacUserProfileForm(options: {
  translate: Translate;
  tenantName: string;
  userRef: string;
  profile: LacUser;
  trimmedUsername: string;
  trimmedEmail: string;
  firstName: string;
  lastName: string;
  validFrom: string;
  validTo: string;
  role: LacClientRole;
  loadedRole: LacClientRole;
  isRoleLoadError: boolean;
  rolesData: string[] | undefined;
  photoChanged: boolean;
  pendingFile: File | null;
  removePhoto: boolean;
  updateUserMutation: MutationLike<LacUpdateUserInput>;
  setUserRolesMutation: MutationLike<LacSetRolesVariables>;
  uploadPhotoMutation: MutationLike<LacUploadPhotoVariables>;
  deletePhotoMutation: MutationLike<LacDeletePhotoVariables>;
  bumpPhotoReloadKey: () => void;
}): void {
  const {
    translate,
    tenantName,
    userRef,
    profile,
    trimmedUsername,
    trimmedEmail,
    firstName,
    lastName,
    validFrom,
    validTo,
    role,
    loadedRole,
    isRoleLoadError,
    rolesData,
    photoChanged,
    pendingFile,
    removePhoto,
    updateUserMutation,
    setUserRolesMutation,
    uploadPhotoMutation,
    deletePhotoMutation,
    bumpPhotoReloadKey,
  } = options;

  const persistPhotoChanges = () => {
    if (pendingFile) {
      uploadPhotoMutation.mutate(
        { tenantName, userRef, file: pendingFile },
        {
          onSuccess: () => {
            toast.success({
              title: translate('lac.users.profile.photoUploadSuccessTitle'),
            });
            bumpPhotoReloadKey();
          },
          onError: (error: unknown) => {
            void toastMutationError(
              error,
              translate('lac.users.profile.photoUploadErrorTitle'),
            );
          },
        },
      );
      return;
    }

    if (removePhoto) {
      deletePhotoMutation.mutate(
        { tenantName, userRef },
        {
          onSuccess: () => {
            toast.success({
              title: translate('lac.users.profile.photoRemoveSuccessTitle'),
            });
            bumpPhotoReloadKey();
          },
          onError: (error: unknown) => {
            void toastMutationError(
              error,
              translate('lac.users.profile.photoRemoveErrorTitle'),
            );
          },
        },
      );
    }
  };

  updateUserMutation.mutate(
    {
      tenantName,
      userRef,
      username: trimmedUsername,
      email: trimmedEmail,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      validFrom: validFrom.trim() || undefined,
      validTo: validTo.trim() || undefined,
      ...pickUserOptimisticLockFields({
        id: profile.id,
        version: profile.version,
        stamp: profile.stamp,
        created: profile.created,
      }),
    },
    {
      onSuccess: () => {
        toast.success({
          title: translate('lac.users.profile.saveSuccessTitle'),
          description: translate('lac.users.profile.saveSuccessDescription', {
            username: trimmedUsername,
          }),
        });

        const shouldUpdateRole =
          !isRoleLoadError && rolesData != null && role !== loadedRole;
        if (shouldUpdateRole) {
          setUserRolesMutation.mutate(
            {
              tenantName,
              userRef,
              roleNames: [role],
            },
            {
              onError: (error: unknown) => {
                void toastMutationError(
                  error,
                  translate('lac.users.profile.roleErrorTitle'),
                  translate('lac.users.profile.roleErrorDescription'),
                );
              },
            },
          );
        }

        if (photoChanged) {
          persistPhotoChanges();
        }
      },
      onError: (error: unknown) => {
        void toastMutationError(
          error,
          translate('lac.users.profile.saveErrorTitle'),
          translate('lac.users.profile.saveErrorDescription'),
        );
      },
    },
  );
}
